import React from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

export type LeagueStatus = 'active' | 'locked' | 'offseason';

export interface LeagueTool {
  name: string;
  description?: string;
  path: string;
}

export interface LeagueCardProps {
  league: string;
  leagueIcon?: string;
  status: LeagueStatus;
  statusLabel?: string;
  statusTime?: string;
  tools: LeagueTool[];
  onUnlock?: () => void;
  buttonText?: string;
}

const LeagueCard: React.FC<LeagueCardProps> = ({
  league,
  leagueIcon,
  status,
  statusLabel,
  statusTime,
  tools,
  onUnlock,
  buttonText,
}) => {
  const history = useHistory();

  const getStatusColor = () => {
    switch (statusLabel?.toLowerCase()) {
      case 'upcoming':
        return '#FF6B00';
      case 'live':
        return '#22C55E';
      case 'offseason':
        return 'rgba(255, 255, 255, 0.5)';
      default:
        return 'rgba(255, 255, 255, 0.5)';
    }
  };

  const handleToolClick = (path: string) => {
    if (status === 'active') {
      history.push(path);
    }
  };

  const handlePrimaryCTA = () => {
    if (status === 'active' && tools.length > 0) {
      // Navigate to the first tool (Contest Generator or first available)
      const contestGenTool = tools.find(t => t.name.toLowerCase().includes('contest simulations'));
      const targetTool = contestGenTool || tools[0];
      history.push(targetTool.path);
    }
  };

  const isClickable = status === 'active';

  // Get border color based on status
  const getBorderColor = () => {
    switch (status) {
      case 'active':
        return 'rgba(255, 255, 255, 0.2)';
      case 'locked':
        return 'rgba(74, 158, 255, 0.3)';
      case 'offseason':
        return 'rgba(255, 255, 255, 0.1)';
      default:
        return 'rgba(255, 255, 255, 0.1)';
    }
  };

  return (
    <Box
      sx={{
        bgcolor: '#141414',
        borderRadius: '12px',
        border: `1px solid ${getBorderColor()}`,
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minHeight: status === 'offseason' ? 'auto' : 380,
      }}
    >
      {/* Header: League icon and name */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
        {leagueIcon && (
          <Box
            component="img"
            src={leagueIcon}
            alt={league}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '4px',
              objectFit: 'contain',
            }}
          />
        )}
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: 18,
            color: 'white',
          }}
        >
          {league}
        </Typography>
        {status === 'locked' && (
          <LockOutlinedIcon
            sx={{
              fontSize: 18,
              color: 'rgba(255, 255, 255, 0.5)',
            }}
          />
        )}
      </Stack>

      {/* Status badge and time */}
      {status !== 'offseason' && (
        <>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: 12,
              color: getStatusColor(),
              mb: 0.5,
            }}
          >
            {statusLabel}
          </Typography>
          {statusTime && (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
                fontSize: 12,
                color: 'rgba(255, 255, 255, 0.5)',
                mb: 2,
              }}
            >
              {statusTime}
            </Typography>
          )}
        </>
      )}

      {/* Offseason state */}
      {status === 'offseason' && (
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: 12,
            color: 'rgba(255, 255, 255, 0.5)',
            mb: 2,
          }}
        >
          No Slates Available
        </Typography>
      )}

      {/* Tools list */}
      {status !== 'offseason' && (
        <Stack spacing={2} sx={{ flex: 1, mb: 3 }}>
          {tools.map((tool) => (
            <Box
              key={tool.name}
              onClick={() => handleToolClick(tool.path)}
              sx={{
                cursor: isClickable ? 'pointer' : 'default',
                '&:hover': isClickable
                  ? {
                      '& .tool-name': {
                        color: '#4DA3FF',
                      },
                    }
                  : {},
              }}
            >
              <Typography
                className="tool-name"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: 14,
                  color: 'white',
                  mb: 0.25,
                  transition: 'color 0.2s',
                }}
              >
                {tool.name}
              </Typography>
              {tool.description && status === 'active' && (
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: 12,
                    color: 'rgba(255, 255, 255, 0.5)',
                    lineHeight: 1.4,
                  }}
                >
                  {tool.description}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {/* Action button */}
      <Box sx={{ mt: 'auto' }}>
        {status === 'active' && (
          <Button
            variant="contained"
            onClick={handlePrimaryCTA}
            startIcon={<PlayArrowIcon />}
            fullWidth
            sx={{
              bgcolor: '#4A9EFF',
              color: '#fff',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              borderRadius: '8px',
              py: 1.25,
              '&:hover': {
                bgcolor: '#3A8EEF',
              },
            }}
          >
            {buttonText || 'Build Contest Simulations'}
          </Button>
        )}

        {status === 'locked' && (
          <Button
            variant="outlined"
            onClick={onUnlock}
            endIcon={<LockOutlinedIcon sx={{ fontSize: 16 }} />}
            fullWidth
            sx={{
              borderColor: 'rgba(74, 158, 255, 0.5)',
              color: '#4A9EFF',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              borderRadius: '8px',
              py: 1.25,
              '&:hover': {
                borderColor: 'rgba(74, 158, 255, 0.8)',
                bgcolor: 'rgba(74, 158, 255, 0.1)',
              },
            }}
          >
            Upgrade
          </Button>
        )}

        {status === 'offseason' && (
          <Button
            variant="outlined"
            disabled
            fullWidth
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.3)',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              borderRadius: '8px',
              py: 1.25,
              '&.Mui-disabled': {
                borderColor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            Unavailable
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default LeagueCard;
